// routes/resource-directory.js — 자료실의 **찾기 규칙**.
//
// 이 화면이 고쳐야 했던 것: 목록이 한 덩어리라 자료가 늘면 찾을 방법이 스크롤뿐이었고,
// 고른 자료의 정보가 그 긴 목록 **아래**에 붙어서 고른 순간 화면 밖으로 나갔다.
// 그리고 모든 행이 「검증된 위치」라고 말했다 — Resource Cloud 가 확인해 준 것은
// **등록된 형식과 그 판의 불변성**이지 그 주소가 지금 살아 있다는 사실이 아니다.
// 확인하지 않은 것을 확인했다고 적으면, 진짜로 확인한 것까지 믿을 수 없게 된다.
//
// 그래서 검색·필터·요약은 전부 여기 순수 함수로 있고, 화면은 이 값을 그리기만 한다.
// **여기에는 네트워크가 없다.** 미리보기를 만들려고 외부 주소를 열어 보지 않는다 —
// 그것은 이 Space 밖으로 나가는 요청이고, 사람이 시키지 않은 일이다.

export const KIND_LABELS = Object.freeze({
  text: '문서',
  image: '이미지',
  url: 'URL',
  video: '동영상',
});

/** 모르는 종류는 지어내지 않고 서버가 준 값을 그대로 보인다. */
export function kindLabel(kind) {
  return KIND_LABELS[kind] ?? String(kind ?? '알 수 없음');
}

/** 주소를 다시 찾는 방식. `kind`(무엇인가)와 **다른 축**이다. */
export function sourceTypeLabel(type) {
  if (type === 'git') return 'git 저장소의 파일';
  if (type === 'uri') return 'URL 하나';
  // Resource Cloud 가 직접 보관하는 파일. 밖으로 나가는 주소가 없고(`sourceUri: null`),
  // 내려받기는 매 요청 권한을 다시 보는 인증된 경로 하나뿐이다.
  if (type === 'managed_file') return 'Loom이 보관하는 파일';
  if (type === 'unknown') return '알 수 없는 방식';
  return String(type ?? '알 수 없음');
}

/**
 * 이 자료의 주소 방식. **모르는 것을 `uri` 로 치지 않는다.**
 *
 * 예전 기본값이 `'uri'` 였다: 서버가 새 방식을 하나 더 내보내는 순간(`managed_file` 이
 * 실제로 그랬다) 화면은 그걸 전부 「URL」이라고 부르고, 없는 주소를 읽으려다 실패한
 * 자리에 「주소를 읽지 못함」이라고 적는다. 둘 다 사실이 아니다.
 */
export function sourceTypeOf(asset) {
  const type = asset?.source?.type;
  return typeof type === 'string' && type !== '' ? type : 'unknown';
}

/**
 * 형식을 사람의 말로. `application/pdf` 는 기계의 이름이고, 화면에서 먼저 읽혀야 하는 것은
 * 「PDF」다. 모르는 형식은 **그대로 적는다** — 지어내면 그것이 무엇인지 알 수 없어진다.
 */
const MEDIA_TYPE_LABELS = Object.freeze({
  'application/pdf': 'PDF',
  'image/png': 'PNG 이미지',
  'image/jpeg': 'JPEG 이미지',
  'text/plain': '텍스트',
  'text/markdown': 'Markdown',
});

export function mediaTypeLabel(mediaType) {
  if (typeof mediaType !== 'string' || mediaType === '') return '형식을 읽지 못함';
  return MEDIA_TYPE_LABELS[mediaType] ?? mediaType;
}

/** 밖으로 나가는 주소가 있는 자료인가. 없으면 「원문 열기」를 그리지 않는다. */
export function hasExternalOriginal(asset) {
  return typeof asset?.sourceUri === 'string' && asset.sourceUri !== '' && !asset?.sourceRedacted;
}

/** 사람이 읽을 크기. 서버가 준 바이트 수가 없으면 지어내지 않는다. */
export function fileSizeLabel(byteSize) {
  if (!Number.isFinite(byteSize) || byteSize < 0) return null;
  if (byteSize < 1024) return `${byteSize}B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = byteSize / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)}${units[unit]}`;
}

/**
 * 주소에서 사람이 알아볼 자리 이름만.
 *
 * 파싱에 실패하면 **null 이다** — 원문 문자열을 잘라 호스트인 척하지 않는다.
 */
export function assetHost(sourceUri) {
  try {
    const url = new URL(String(sourceUri ?? ''));
    return url.host || null;
  } catch {
    return null;
  }
}

/**
 * 행에 적을 한 줄. **가진 사실만 적는다.**
 *
 * git 자료는 주소 하나로 다시 찾을 수 없어서 경로와 commit 앞자리를 함께 적고,
 * URL 자료는 자리 이름만 적는다. 자격으로 보이는 값이 가려진 주소는 그 사실을 말한다.
 */
export function assetSourceSummary(asset) {
  if (asset?.sourceRedacted) return '주소 일부가 가려진 등록';
  const type = sourceTypeOf(asset);
  if (type === 'managed_file') {
    // 보관 파일에는 외부 주소가 없다. 서버가 준 이름·크기만 적고, 없으면 없다고 적는다.
    const size = fileSizeLabel(asset.file?.byteSize);
    const name = asset.file?.filename ?? '이름을 읽지 못한 파일';
    return size ? `보관 파일 · ${name} · ${size}` : `보관 파일 · ${name}`;
  }
  if (type === 'git') {
    const commit = String(asset.source.commit ?? '').slice(0, 12);
    const path = asset.source.path ?? '경로 없음';
    return commit ? `git · ${path} · ${commit}` : `git · ${path}`;
  }
  if (type === 'uri') {
    const host = assetHost(asset?.sourceUri);
    return host ? `URL · ${host}` : 'URL · 주소를 읽지 못함';
  }
  // 이 화면이 모르는 방식이다. **URL 이라고 부르지 않는다** — 서버가 말한 이름을 그대로 적는다.
  return type === 'unknown' ? '주소 방식을 읽지 못한 등록' : `알 수 없는 주소 방식 · ${type}`;
}

/** 이 자료가 가리키는 판을 한 줄로. 없는 판을 1 로 채우지 않는다. */
export function assetVersionLabel(asset) {
  const version = asset?.registeredVersion ?? asset?.currentVersion ?? null;
  const registered = version === null ? '등록 판 없음' : `등록 판 v${version}`;
  return asset?.revision ? `${registered} · 원본 표기 ${asset.revision}` : registered;
}

/**
 * 검색 대상은 **화면에 보이는 글자**다.
 *
 * 제목·종류·판·출처 요약까지다. id 나 전체 주소처럼 보이지 않는 값을 몰래 훑으면
 * 왜 걸렸는지 설명할 수 없는 결과가 나온다.
 */
export function assetSearchText(asset) {
  return [
    asset?.title,
    kindLabel(asset?.kind),
    asset?.revision,
    assetSourceSummary(asset),
  ].filter(Boolean).join(' ').toLowerCase();
}

/** 공백은 AND 다. 좁히려고 더 적는 것이 자연스럽다. */
export function matchesAssetQuery(asset, query) {
  const terms = String(query ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const text = assetSearchText(asset);
  return terms.every((term) => text.includes(term));
}

/** 검색·필터를 함께 적용한다. 순서는 서버가 준 순서 그대로다. */
export function filterAssets(assets, { query = '', kind = 'all', source = 'all' } = {}) {
  return (assets ?? []).filter((asset) => {
    if (kind !== 'all' && asset.kind !== kind) return false;
    if (source !== 'all' && sourceTypeOf(asset) !== source) return false;
    return matchesAssetQuery(asset, query);
  });
}

/**
 * 필터 선택지는 **실제로 있는 값에서만** 만든다.
 *
 * 고정 목록을 그리면 「이미지(0)」 같은 칸이 늘 보이고, 사람은 그걸 고른 뒤 빈 화면을
 * 보게 된다. 값이 한 종류뿐이면 필터 자체를 만들지 않는다 — 고를 것이 없는 컨트롤이다.
 */
export function facetOptions(assets) {
  const kinds = new Map();
  const sources = new Map();
  for (const asset of assets ?? []) {
    kinds.set(asset.kind, (kinds.get(asset.kind) ?? 0) + 1);
    const type = sourceTypeOf(asset);
    sources.set(type, (sources.get(type) ?? 0) + 1);
  }
  const total = (assets ?? []).length;
  const build = (map, label) => (map.size < 2 ? [] : [
    { value: 'all', label: '전체', count: total },
    ...[...map.entries()].map(([value, count]) => ({ value, label: label(value), count })),
  ]);
  return {
    kinds: build(kinds, kindLabel),
    sources: build(sources, sourceTypeLabel),
  };
}

/**
 * 지금 목록이 처한 상태. **"아직 없다"와 "조건에 안 걸린다"는 다른 말이다.**
 * 앞의 것은 자료를 연결하라고 안내할 자리이고, 뒤의 것은 조건을 지우라고 안내할 자리다.
 */
export function directoryState({ total, visible, filtersActive }) {
  if ((total ?? 0) === 0) return 'empty';
  if ((visible ?? 0) === 0 && filtersActive) return 'no-results';
  return 'listing';
}

/** 검색어·필터 중 하나라도 걸려 있나. */
export function filtersActive({ query = '', kind = 'all', source = 'all' } = {}) {
  return String(query ?? '').trim() !== '' || kind !== 'all' || source !== 'all';
}

/**
 * 주소줄과 화면을 잇는 규칙.
 *
 * 이 화면의 상태(무엇을 찾고 있었나 · 무엇을 고르고 있었나)는 URL 에 있다. 그래야
 * 새로고침·뒤로가기·링크 공유가 같은 자리를 연다. 없는 값은 **주소에 적지 않는다** —
 * `?q=&kind=all` 같은 빈 꼬리표는 공유했을 때 읽는 사람을 헷갈리게 한다.
 */
export function readDirectoryParams(search) {
  const params = new URLSearchParams(String(search ?? '').replace(/^\?/, ''));
  const value = (name, fallback) => {
    const raw = params.get(name);
    return raw === null || raw === '' ? fallback : raw;
  };
  return {
    query: value('q', ''),
    kind: value('kind', 'all'),
    source: value('source', 'all'),
    assetId: value('asset', null),
    /**
     * **어느 판을 보자는 것인가.**
     *
     * 없으면 현재 판이다. 있으면 그 판이고, 목록이 더 새 판을 알고 있어도 **바꿔 주지
     * 않는다** — 넘김이 v1 로 고정해 둔 근거를 열었는데 v2 가 보이면, 그 사람은 자기가
     * 받은 것과 다른 것을 읽으면서 같은 것을 읽었다고 믿게 된다.
     */
    version: value('version', null),
  };
}

export function writeDirectoryParams({
  query = '', kind = 'all', source = 'all', assetId = null, version = null,
} = {}) {
  const params = new URLSearchParams();
  if (String(query ?? '').trim() !== '') params.set('q', query);
  if (kind && kind !== 'all') params.set('kind', kind);
  if (source && source !== 'all') params.set('source', source);
  if (assetId) params.set('asset', assetId);
  // 판은 고른 자료가 있을 때만 뜻이 있다. 자료 없이 판만 적힌 주소는 만들지 않는다.
  if (assetId && version) params.set('version', String(version));
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/**
 * 자료 하나(그리고 필요하면 **그 판**)로 가는 주소.
 *
 * Task 의 근거·넘김 꾸러미에서 이 주소를 만든다. 고정된 참조는 반드시 판을 함께 싣는다 —
 * 그것이 「그때의 그것」과 「지금의 그것」을 가르는 유일한 값이다.
 */
export function resourceLink(spaceId, assetId, version = null) {
  return `/s/${spaceId}/resources${writeDirectoryParams({ assetId, version })}`;
}

/** 이 화면이 지금 보는 판. 요청한 판이 있으면 그것, 없으면 목록이 아는 현재 판이다. */
export function shownVersion(asset, requested = null) {
  if (requested) return String(requested);
  const current = asset?.registeredVersion ?? asset?.currentVersion ?? null;
  return current === null ? null : String(current);
}

/** 요청한 판이 이 자료의 현재 판보다 뒤인가 — 화면이 그 사실을 말할 자리. */
export function isPinnedToOlderVersion(asset, requested = null) {
  if (!requested) return false;
  const current = asset?.currentVersion ?? asset?.registeredVersion ?? null;
  if (current === null) return false;
  return String(requested) !== String(current);
}

/**
 * 주소가 가리키는 자료를 목록에서 찾는다.
 *
 * 못 찾으면 **null 이고, 화면은 그 사실을 말한다.** 대신 첫 항목을 골라 주면 사람은
 * 자기가 연 링크가 이 자료였다고 믿게 된다.
 */
export function findAsset(assets, assetId) {
  if (!assetId) return null;
  return (assets ?? []).find((asset) => asset.assetId === assetId) ?? null;
}
